import { Platform } from "@-/com.npmjs.react-native";
import {
  type SharedValue,
  useAnimatedReaction,
  useFrameCallback,
  useReducedMotion,
  useSharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { getGraphicsTier } from "@-/dev.improve.haniverse.lib.graphics-tier";
import { useVisualActivity } from "@-/dev.improve.haniverse.lib.visual-activity";
import onetime from "@-/com.npmjs.onetime";
import { useCallback, useEffect, useState } from "react";
import {
  WELCOME_GALAXY_INTRO_END_T,
  WELCOME_GALAXY_SETTLE_T,
  WELCOME_GALAXY_SETTLED_T,
  welcomeGalaxySceneJoinTime,
} from "#welcome-galaxy-timeline";

/** How much faster the timeline runs while fast-forwarding. */
const FAST_FORWARD_SPEED = 5;
/** How quickly the playback speed eases towards its target (1/s). */
const SPEED_RESPONSE = 8;
/** Longest frame step, so a hitch (or a return from the background) never jumps the timeline. */
const MAX_FRAME_SECONDS = 0.05;

/**
 * The shortest step the galaxy's clocks take (s), since every step recomputes and redraws the whole
 * scene: on a 90–120 Hz Android panel (common on phones whose GPUs are not) up to twice as often
 * as 60 Hz would, for much the same picture. Android steps at no more than 60 to 90 Hz, and a
 * `low` graphics tier phone at no more than 60 Hz anywhere, and 30 Hz once the galaxy has settled
 * into its slow spin. iPhones keep every frame, which their GPUs have room for. (A little under
 * each period, so a 60 Hz panel's frames are never skipped for arriving a touch early.)
 */
export const getWelcomeGalaxyMinStepSeconds = onetime((): { active: number; settled: number } => {
  const isLowTier = getGraphicsTier() === "low";
  const active = isLowTier ? 0.85 / 60 : Platform.OS === "android" ? 0.01 : 0;
  return { active, settled: isLowTier ? 0.85 / 30 : active };
});

/**
 * How long after the page is revealed (once the native splash is down, on a cold launch) its clock
 * starts: a fixed beat on the page's first, still frame (the splash's copy, the picture of the app
 * after a sign-out, or the flight's first frame), about what a warm shader cache needs to compile
 * the Skia scene's GPU pipelines ahead of time (~1 s from the canvas's mount, which comes before the
 * splash has finished fading). So on a warm cache the scene is ready when the clock starts and the
 * whole launch plays on it. It never waits for the warm-up itself: a cold cache (4.6–5.1 s on the
 * iOS simulator) only changes what is drawn behind the headline and buttons, never when they
 * appear. Web has no warm-up (CanvasKit's offscreen surfaces are CPU rasters) and starts at once.
 */
export const WELCOME_GALAXY_START_DELAY_MS = Platform.OS === "web" ? 0 : 800;

/** The welcome galaxy's clocks between two frames (see `stepWelcomeGalaxyClock`). */
export type WelcomeGalaxyClockState = {
  /** Whether the Skia scene has joined the page's clock; until then its time stands still. */
  isSceneLive: boolean;
  /** The Skia scene's time (s): what its canvas, and the entry host's, draws. */
  sceneTime: number;
  /** The playback speed, easing towards 1 or the fast-forward speed. */
  speed: number;
  /**
   * The page's time (s): the headline, the buttons and the launch's sky and splash follow it, from
   * the moment the clock starts, whether or not the scene is ready.
   */
  time: number;
};

/**
 * One frame of the welcome galaxy's clocks, `frameSeconds` long. The page's time always advances;
 * the scene's advances with it once the scene is live, and stands still before. Nothing here
 * depends on the GPU warm-up, so the headline and buttons keep the same schedule however long it
 * takes.
 */
export function stepWelcomeGalaxyClock(
  state: WelcomeGalaxyClockState,
  frameSeconds: number,
  isFastForwarding: boolean,
): WelcomeGalaxyClockState {
  "worklet";
  const seconds = Math.min(frameSeconds, MAX_FRAME_SECONDS);
  const targetSpeed =
    isFastForwarding && state.time < WELCOME_GALAXY_SETTLE_T ? FAST_FORWARD_SPEED : 1;
  const speed = state.speed + (targetSpeed - state.speed) * Math.min(1, seconds * SPEED_RESPONSE);
  const step = seconds * speed;
  return {
    isSceneLive: state.isSceneLive,
    sceneTime: state.isSceneLive ? state.sceneTime + step : state.sceneTime,
    speed,
    time: state.time + step,
  };
}

/**
 * The Skia scene joins the page's clock: in step while the stand-in has shown only the sky, or on
 * the settled galaxy the stand-in has been resolving (`welcomeGalaxySceneJoinTime`). A scene that
 * is already live keeps its time.
 */
export function joinWelcomeGalaxyScene(state: WelcomeGalaxyClockState): WelcomeGalaxyClockState {
  "worklet";
  if (state.isSceneLive) {
    return state;
  }
  return {
    isSceneLive: true,
    sceneTime: welcomeGalaxySceneJoinTime(state.time),
    speed: state.speed,
    time: state.time,
  };
}

export type WelcomeGalaxyClock = {
  /**
   * Speed the timeline up until the galaxy has settled (a touch on the galaxy). A worklet, so the
   * galaxy gesture can call it straight from the UI thread.
   */
  fastForward: () => void;
  /**
   * The page's time, held at `WELCOME_GALAXY_INTRO_END_T` once it gets there: for everything
   * outside the Skia scene (the launch's splash and sky, the stand-in, the headline, the buttons),
   * whose curves have all finished by then. Once it stops changing, the views and reactions that
   * read it stop running every frame while the galaxy idles.
   */
  introTime: SharedValue<number>;
  /**
   * Whether the Skia scene has joined the page's clock: until then the stand-in
   * (`WelcomeGalaxyStandIn`) is drawn in its place, and it crossfades away once this is set.
   */
  isSceneLive: SharedValue<boolean>;
  /** The Skia scene's seconds of simulated time: the single input to every scene curve. */
  simTime: SharedValue<number>;
};

/**
 * The welcome galaxy's clock, shared by the Skia scene and the overlay so they follow one
 * choreography. It lives above the canvas, so the text and buttons animate on schedule while the
 * canvas loads (CanvasKit, on web) or compiles its GPU pipelines (`isSceneReady`).
 *
 * Under reduced motion, or with `startSettled` (the static fallback), the clock starts at the
 * settled moment and never runs. Otherwise the page's time starts at `startTime` (before 0 for the
 * launch out of the splash screen or a picture of the app) and runs from
 * `WELCOME_GALAXY_START_DELAY_MS` after the page is revealed (`isRevealed`). The scene's time stands
 * still on its first frame until `isSceneReady`, then joins the page's
 * (`joinWelcomeGalaxyScene`). Once the entry into the Haniverse has begun (`entryTime` > 0) the
 * clock stops and the entry host drives the scene's time; a scene that had not joined yet joins
 * settled, since the entry starts from the settled galaxy.
 */
export function useWelcomeGalaxyClock({
  entryTime,
  isRevealed,
  isSceneReady,
  startSettled,
  startTime,
}: {
  entryTime: SharedValue<number>;
  /** Whether the page is on screen: false while a cold launch's native splash is still up. */
  isRevealed: boolean;
  /** Whether the Skia scene can draw without stalling: its canvas is up and its pipelines ready. */
  isSceneReady: boolean;
  startSettled: boolean;
  startTime: number;
}): WelcomeGalaxyClock {
  const reduceMotion = useReducedMotion();
  const isStatic = reduceMotion || startSettled;
  const initialTime = isStatic ? WELCOME_GALAXY_SETTLED_T : startTime;
  const time = useSharedValue(initialTime);
  const simTime = useSharedValue(initialTime);
  const introTime = useSharedValue(Math.min(initialTime, WELCOME_GALAXY_INTRO_END_T));
  const speed = useSharedValue(1);
  const isFastForwarding = useSharedValue(false);
  const isSceneLive = useSharedValue(false);
  // Time since the scene last stepped, while frames are skipped (`getWelcomeGalaxyMinStepSeconds`).
  const pendingSeconds = useSharedValue(0);
  const minStepSeconds = getWelcomeGalaxyMinStepSeconds();
  const sceneReady = useSharedValue(false);
  const isVisuallyActive = useVisualActivity();
  const [isStarted, setIsStarted] = useState(false);

  // A fixed beat after the reveal, never longer: see `WELCOME_GALAXY_START_DELAY_MS`.
  useEffect(() => {
    if (!isRevealed) {
      return;
    }
    const startDelay = setTimeout(setIsStarted, WELCOME_GALAXY_START_DELAY_MS, true);
    return () => {
      clearTimeout(startDelay);
    };
  }, [isRevealed]);

  useEffect(() => {
    sceneReady.set(isSceneReady);
  }, [isSceneReady, sceneReady]);

  // The scene joins once it is ready, or settled once the entry begins, whichever comes first.
  // A reaction rather than the frame callback, which does not run while the clock is held, static,
  // or stopped for the entry.
  useAnimatedReaction(
    () => {
      if (entryTime.get() > 0) {
        return "entry";
      }
      return sceneReady.get() ? "ready" : null;
    },
    (reason) => {
      if (reason === null || isSceneLive.get()) {
        return;
      }
      simTime.set(
        reason === "entry"
          ? WELCOME_GALAXY_SETTLED_T
          : joinWelcomeGalaxyScene({
              isSceneLive: false,
              sceneTime: simTime.get(),
              speed: speed.get(),
              time: time.get(),
            }).sceneTime,
      );
      isSceneLive.set(true);
    },
  );

  const frameCallback = useFrameCallback((frame) => {
    "worklet";
    if (entryTime.get() > 0) {
      return;
    }
    const frameSeconds = pendingSeconds.get() + (frame.timeSincePreviousFrame ?? 16) / 1000;
    const isSettled = simTime.get() >= WELCOME_GALAXY_SETTLED_T && !isFastForwarding.get();
    if (frameSeconds < (isSettled ? minStepSeconds.settled : minStepSeconds.active)) {
      pendingSeconds.set(frameSeconds);
      return;
    }
    pendingSeconds.set(0);
    const next = stepWelcomeGalaxyClock(
      {
        isSceneLive: isSceneLive.get(),
        sceneTime: simTime.get(),
        speed: speed.get(),
        time: time.get(),
      },
      frameSeconds,
      isFastForwarding.get(),
    );
    speed.set(next.speed);
    time.set(next.time);
    // Setting the same value again notifies nobody.
    introTime.set(Math.min(next.time, WELCOME_GALAXY_INTRO_END_T));
    // A scene that has not joined stays on its first frame, so its canvas does not redraw (and
    // contend for the GPU) while the warm-up compiles its pipelines.
    if (next.isSceneLive) {
      simTime.set(next.sceneTime);
    }
  }, false);

  const { setActive } = frameCallback;
  useEffect(() => {
    setActive(isVisuallyActive && !isStatic && isStarted);
    return () => {
      setActive(false);
    };
  }, [isStarted, isStatic, isVisuallyActive, setActive]);

  const fastForward = useCallback(() => {
    "worklet";
    isFastForwarding.set(true);
  }, [isFastForwarding]);

  return { fastForward, introTime, isSceneLive, simTime };
}
